// filepath: apps/web/src/design-system/error-states/StaleVersionError.tsx
//
// Rendered when a pinned version is no longer available (FR-BPM-10).
// Per ARCHITECTURE_WEB.md §4: never silently substitute a different
// version. The user must start over with the active version.

import React from "react";

export interface StaleVersionErrorProps {
  /**
   * Optional human-readable entity name (e.g. "Acme Corp") so the
   * surface can name what the user was working with before the
   * version went stale. Default copy is generic ("this form").
   */
  entityName?: string;
  /**
   * Optional reload handler. When provided, surfaces a "Reload" action
   * that the user can use to refetch the active version. Without it,
   * the surface stays read-only with the support-contact copy.
   */
  onReload?: () => void;
  /** Test hook forwarded onto the rendered element. */
  "data-testid"?: string;
  /** Extra className passthrough. */
  className?: string;
}

export function StaleVersionError({
  entityName,
  onReload,
  "data-testid": testId = "form-stale-version",
  className,
}: StaleVersionErrorProps = {}): React.ReactElement {
  const subject = entityName ? `“${entityName}”` : "this form";
  return (
    <div
      data-testid={testId}
      className={["form-error", "form-stale-version", className]
        .filter(Boolean)
        .join(" ")}
      role="alert"
    >
      <h2>This version is no longer available</h2>
      <p>
        The version of {subject} you started with is no longer retrievable.
        {onReload
          ? " Use Reload to fetch the active version and start over."
          : " Contact support if the issue persists."}
      </p>
      {onReload ? (
        <button
          type="button"
          onClick={onReload}
          className="form-stale-version__reload"
          data-testid={`${testId}-reload`}
        >
          Reload
        </button>
      ) : null}
    </div>
  );
}
